import { Component, OnInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LineAreaChartComponent } from '../../../shared/components/line-area-chart/line-area-chart.component';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { CampaignFilterComponent, CampaignPerformanceRequest } from '../../../shared/components/campaign-filter/campaign-filter.component';
import { ChartSeries } from '../../../shared/models/chart.model';
import { ChartColors } from '../../../core/config/chart.config';
import { HumCampaignsService } from './hum-campaigns.service';
import { TruncatePipe } from '../../../shared/pipes/truncate.pipe';
import { 
  HumFunnel,
  ImpressionsVsConversion, 
  HumCampaignFilterOptions 
} from '../hum-campaigns.model';
import { HttpClient } from '@angular/common/http';
import { finalize } from 'rxjs/operators';

// Define chart types for loading states
type ChartType = 'funnel' | 'impressionsVsConversion';

interface FunnelStage {
  stage: string;
  count: number;
  percentage: number;
}

// Add this at the top of the file, outside the class
const ALL_CAMPAIGNS_CODE = 'All Campaigns';

/**
 * HUM Campaigns Component
 * 
 * Displays HUM campaign analytics including:
 * - Editorial-style funnel visualization
 * - Conversions vs Dismissals trends
 */
@Component({
  selector: 'app-hum-campaigns',
  templateUrl: './hum-campaigns.component.html',
  styleUrls: ['./hum-campaigns.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LineAreaChartComponent,
    LoaderComponent,
    CampaignFilterComponent
  ]
})
export class HumCampaignsComponent implements OnInit {
  // Loading states
  // loading: boolean = false; 

  loadingStates: { [key: string]: boolean } = {
    funnel: false,
    impressionsVsConversion: false
  };
  
  // Data
  funnelStages: FunnelStage[] = [];
  impressionsVsConversionData: ImpressionsVsConversion[] = [];
  
  // Current filter options - will be set by the campaign filter component
  currentFilters: HumCampaignFilterOptions | null = null;
  
  // Series configurations for conversions vs dismissals chart
  impressionsVsConversionSeries: ChartSeries[] = [
    {
      key: 'connectedConversion',
      name: 'Connected Conversions',
      color: ChartColors.primary
    },
    {
      key: 'totalConversion',
      name: 'Total Conversions',
      color: ChartColors.secondary
    } 
  ];

  // Editorial funnel color sequence - matches the editorial pattern
  editorialColors: string[] = [
    '#0747A6',  // Dark blue - first stage (like "Submission Received")
    '#00B8D9',  // Cyan - second stage (like "Assigned to Reviewer") 
    '#36B37E',  // Green - third stage (like "Review Received")
    '#FF5630',  // Coral red - fourth stage (like "Decision Made")
    '#6554C0',  // Purple - fifth stage (like "Accepted")
    '#FFAB00'   // Orange - sixth stage (like "Published")
  ];

  showCampaignFunnelInfo = false;
  
  campaignFunnelLegendInfo = [
    {
      key: 'Unique Impressions',
      definition: 'Number of unique users who saw the campaign.'
    },
    {
      key: 'Cancelled',
      definition: 'The number of people who closed the campaign by clicking the x or on the browser window.'
    },
    {
      key: 'Conversions',
      definition: 'The number of people who either clicked the CTA button or completed the form for the campaign.'
    },
    {
      key: 'Total Dismissals',
      definition: 'The number of people who clicked “Do not show this message again”. This number may show null or 0 if you do not have this set for your campaign.'
    }
    ];

  // Campaign funnel color sequence
  campaignColors: string[] = [
    '#0747A6',  // Blue - Impressions
    '#FF5630',  // Red - Dismissals
    '#36B37E',   // Green - Conversions
    '#6554C0'   // Purple - Conversions
  ];

  showConversionsVsDismissalsInfo = false;
  conversionsVsDismissalsLegendInfo = [
    {
      key: 'Connected Conversions',
      definition: 'The number of connected profiles who either clicked the CTA button or completed the form for the campaign'
    },
    {
      key: 'Total Conversions',
      definition: 'The number of people who either clicked the CTA button or completed the form for the campaign'
    } 
  ];

  @ViewChild(CampaignFilterComponent) campaignFilter!: CampaignFilterComponent;

  selectedCampaignName: string | null = ALL_CAMPAIGNS_CODE;

  constructor(
    private humCampaignsService: HumCampaignsService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient
  ) {}
  
  // Helper methods for loading states
  hasAnyLoadingState(): boolean {
    return Object.values(this.loadingStates).some(state => state);
  }

  setLoadingState(key: string, loading: boolean) {
    this.loadingStates[key] = loading;
  }

  resetAllLoadingStates() {
    Object.keys(this.loadingStates).forEach(key => {
      this.loadingStates[key] = false;
    });
  }

  ngOnInit(): void {
    // Set a default date range: fromDate = January of current year, toDate = current month
    const today = new Date();
    const toYear = today.getFullYear();
    const toMonth = today.getMonth() + 1;
    const toDate = `${toYear}${toMonth.toString().padStart(2, '0')}`;

    // fromDate is January of current year
    const fromDate = `${toYear}01`;

    this.currentFilters = {
      fromDate: fromDate, // YYYYMM format (January)
      toDate: toDate,     // YYYYMM format (current)
      campaignCode: null
    };
    this.loadAllData();
  }

  /**
   * Handle campaign selection from the campaign filter component
   */
  onCampaignSelected(performanceData: CampaignPerformanceRequest): void {
    this.loadingStates['funnel'] = true;
    this.loadingStates['impressionsVsConversion'] = true;
    
    this.selectedCampaignName = performanceData.campaignName;
    
    if(performanceData.campaignCode === ALL_CAMPAIGNS_CODE){
      this.selectedCampaignName = ALL_CAMPAIGNS_CODE;
    }
    // Create filter options with the date range and campaign selection
    this.currentFilters = {
      fromDate: performanceData.fromDate, // Already in YYYYMM format
      toDate: performanceData.toDate // Already in YYYYMM format
    };
    
    // Only add campaignId if a specific campaign is selected (not ALL_CAMPAIGNS_CODE)
    if (performanceData.campaignCode && performanceData.campaignCode !== ALL_CAMPAIGNS_CODE) {
      this.currentFilters.campaignCode = performanceData.campaignCode;
    }
    
    this.loadAllData();
  }

  /**
   * Load all chart data
   */
  private loadAllData(): void {
    if (!this.currentFilters) {
      console.warn('No filter options available');
      return;
    }
    
    this.loadCampaignFunnel(this.currentFilters);
    this.loadImpressionsVsConversion(this.currentFilters);
  }

  /**
   * Format date for API (YYYYMM format)
   */
  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${year}${month}`;
  }

  /**
   * Load HUM funnel data
   */
  private loadCampaignFunnel(filters: HumCampaignFilterOptions): void {
    this.loadingStates['funnel'] = true;
    
    this.humCampaignsService.getHumFunnel(filters).subscribe({
      next: (data) => {
        // Process the data to add spaces between words in property names
        this.funnelStages = this.processFunnelData(data);
        this.loadingStates['funnel'] = false;
        this.checkAllLoaded();
      },
      error: (error) => {
        console.error('Error loading campaign funnel:', error);
        this.loadingStates['funnel'] = false;
        this.checkAllLoaded();
      }
    });
  }

  /**
   * Process funnel data to add spaces between camelCase words
   */
  private processFunnelData(data: any[]): FunnelStage[] {
    // Define the desired order for funnel stages
    const stageOrder = [
      // 'Total Impressions',
      'Unique Impressions', 
      'Conversion',
      'Conversion Rate',
      'Cancelled',
      'Dismissed'
    ];

    // Create a map of processed data with spaced names
    const processedData = data.map(item => {
      // Convert camelCase property names to spaced format
      const stage = this.addSpacesToCamelCase(item.stage || item.EventType || '');
      return {
        stage: stage,
        count: item.count || item.Total || 0,
        percentage: item.percentage || item.Percentage || 0
      };
    });

    // Sort the data according to the specified order
    return processedData.sort((a, b) => {
      const aIndex = stageOrder.indexOf(a.stage);
      const bIndex = stageOrder.indexOf(b.stage);
      
      // If both stages are in the order array, sort by their position
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      
      // If only one is in the order array, prioritize it
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      
      // If neither is in the order array, maintain original order
      return 0;
    });
  }

  /**
   * Add spaces between camelCase words
   * Example: "UniqueImpressions" -> "Unique Impressions"
   */
  private addSpacesToCamelCase(text: string): string {
    if (!text) return '';
    
    // Add space before capital letters (except the first character)
    return text.replace(/([A-Z])/g, ' $1').trim();
  }

  /**
   * Load conversions vs dismissals data
   */
  private loadImpressionsVsConversion(filters: HumCampaignFilterOptions): void {
    this.loadingStates['impressionsVsConversion'] = true;
    
    this.humCampaignsService.getImpressionsVsConversion(filters).subscribe({
      next: (data) => {
        this.impressionsVsConversionData = data;
        this.loadingStates['impressionsVsConversion'] = false;
        this.checkAllLoaded();
      },
      error: (error) => {
        console.error('Error loading conversions vs dismissals data:', error);
        this.loadingStates['impressionsVsConversion'] = false;
        this.checkAllLoaded();
      }
    });
  }

  /**
   * Check if all data has loaded
   */
  private checkAllLoaded(): void {
    const allLoaded = !Object.values(this.loadingStates).some(loading => loading);
    if (allLoaded) {
      this.loadingStates['funnel'] = false;
      this.loadingStates['impressionsVsConversion'] = false;
      this.cdr.detectChanges();
    }
  }
 
  // Get color by stage index (0, 1, 2, etc.)
  getStageColor(index: number): string {
    return this.editorialColors[index % this.editorialColors.length];
  }

  /**
   * Get the style for campaign funnel progress bars
   */
  getCampaignFunnelBarStyle(index: number): any {
    return {
      'background-color': this.campaignColors[index % this.campaignColors.length],
      'transition': 'width 0.3s ease-in-out'
    };
  }
} 